import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function FacilityJobs() {
    const nav = useNavigate();
    const [professions, setProfessions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: "", profession: "", specialization: "", location_id: "",
        city: "", date: "", start_time: "09:00", end_time: "17:00",
        compensation_gross: 0, description: "", requirements: "",
        min_experience_years: 0, urgent: false, status: "PUBLISHED",
    });

    useEffect(() => {
        api.get("/professions").then(({ data }) => setProfessions(data.data || []));
        api.get("/facilities/me/locations").then(({ data }) => setLocations(data.data || []));
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form, compensation_gross: parseFloat(form.compensation_gross) };
            if (!payload.location_id) delete payload.location_id;
            await api.post("/jobs", payload);
            toast.success("Annuncio pubblicato!");
            nav("/app/annunci");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-3xl mx-auto">
            <h1 className="font-display text-3xl font-bold tracking-tight">Nuovo annuncio</h1>
            <p className="text-sm text-[#5C5C5C] mt-1 mb-6">Pubblica un turno o una collaborazione.</p>
            <form onSubmit={submit} className="hl-card p-6 space-y-5" data-testid="new-job-form">
                <div><Label>Titolo *</Label>
                    <Input required value={form.title} onChange={(e) => update("title", e.target.value)}
                        placeholder="es. Sostituzione Igienista Dentale" data-testid="job-title" /></div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Professione richiesta *</Label>
                        <Select value={form.profession} onValueChange={(v) => update("profession", v)}>
                            <SelectTrigger data-testid="job-profession"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                                {professions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Specializzazione</Label>
                        <Input value={form.specialization} onChange={(e) => update("specialization", e.target.value)} data-testid="job-spec" /></div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Sede</Label>
                        <Select value={form.location_id || undefined} onValueChange={(v) => update("location_id", v)}>
                            <SelectTrigger data-testid="job-location"><SelectValue placeholder={locations.length ? "Seleziona sede" : "Nessuna sede (compila città)"} /></SelectTrigger>
                            <SelectContent>
                                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.label} — {l.city}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Città (se senza sede)</Label>
                        <Input value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="job-city" /></div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    <div><Label>Data *</Label>
                        <Input type="date" required value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="job-date" /></div>
                    <div><Label>Ora inizio *</Label>
                        <Input type="time" required value={form.start_time} onChange={(e) => update("start_time", e.target.value)} data-testid="job-start" /></div>
                    <div><Label>Ora fine *</Label>
                        <Input type="time" required value={form.end_time} onChange={(e) => update("end_time", e.target.value)} data-testid="job-end" /></div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Compenso lordo (€) *</Label>
                        <Input type="number" step="0.01" required value={form.compensation_gross}
                            onChange={(e) => update("compensation_gross", e.target.value)} data-testid="job-comp" /></div>
                    <div><Label>Esperienza minima (anni)</Label>
                        <Input type="number" min={0} value={form.min_experience_years}
                            onChange={(e) => update("min_experience_years", parseInt(e.target.value || 0))} data-testid="job-min-exp" /></div>
                </div>
                <div><Label>Descrizione *</Label>
                    <Textarea rows={4} required value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="job-desc" /></div>
                <div><Label>Requisiti</Label>
                    <Textarea rows={3} value={form.requirements} onChange={(e) => update("requirements", e.target.value)} data-testid="job-req" /></div>
                <div className="flex items-center gap-3">
                    <Switch checked={form.urgent} onCheckedChange={(v) => update("urgent", v)} id="urgent" data-testid="job-urgent" />
                    <Label htmlFor="urgent">Turno urgente</Label>
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => nav("/app/annunci")}>Annulla</Button>
                    <Button type="submit" disabled={busy} className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="publish-job-btn">
                        {busy ? "Pubblicazione…" : "Pubblica annuncio"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
